import omit from 'lodash/omit';

import { updateUserProfile } from '../../extensions/profile-slug/utils/api';
import { nanoid } from 'nanoid';
import { LISTING_STATE_DRAFT, LISTING_STATE_PUBLISHED } from 'util/types';
import moment from 'moment';
import { types as sdkTypes } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import {
  getDefaultTimeZoneOnBrowser,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import { uniqueBy } from '../../util/generators';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { isUserAuthorized } from '../../util/userHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getSession } from '../../extensions/listing-page/utils/listing.utils';
import {
  generateNumberOfSessionFromEndDate,
  getCheapestPrice,
  getTicketTypesPrices,
} from '../../extensions/edit-listing/utils/listing';
import { END_OPTION_SPECIFIC_DATE } from '../../extensions/edit-listing/common/config/sessions';
import { DUPLICATED_EXCEPTION_ID } from '../../extensions/listings/configs/constants';
import { splitMergedTimeSlot } from '../../extensions/listing-page/utils/listing.utils/splitMergedTimeSlot';
import queryAllPages from '../../extensions/common/utils/queryAll';
import listingsApi from '../../extensions/own-listings/utils/listingsApi';

const { UUID } = sdkTypes;

function getListOfMonthsUntilLastSession(startDate, lastSessionRawDate) {
  const months = [];
  let current = moment(startDate).startOf('month');
  const last = moment(lastSessionRawDate).startOf('month');

  while (current.isSameOrBefore(last)) {
    months.push(current.format('YYYY-MM'));
    current = current.add(1, 'month');
  }
  return months;
}

// Return an array of image ids
const imageIds = (images) => {
  // For newly uploaded image the UUID can be found from "img.imageId"
  // and for existing listing images the id is "img.id"
  return images ? images.map((img) => img.imageId || img.id) : null;
};

// After listing creation & update, we want to make sure that uploadedImages state is cleaned
const updateUploadedImagesState = (state, payload) => {
  const { uploadedImages, uploadedImagesOrder } = state;

  // Images attached to listing entity
  const attachedImages = payload?.data?.relationships?.images?.data || [];
  const attachedImageUUIDStrings = attachedImages.map((img) => img.id.uuid);

  // Uploaded images (which are propably not yet attached to listing)
  const unattachedImages = Object.values(state.uploadedImages);
  const duplicateImageEntities = unattachedImages.filter((unattachedImg) =>
    attachedImageUUIDStrings.includes(unattachedImg.imageId?.uuid)
  );
  return duplicateImageEntities.length > 0
    ? {
        uploadedImages: {},
        uploadedImagesOrder: [],
      }
    : {
        uploadedImages,
        uploadedImagesOrder,
      };
};

// `allExceptions` holds `{ id, start, end, seats }`: the route's `ListingException` with
// `start`/`end` as Dates rather than ISO strings, so the calendar code compares them directly.
const toListingException = ({ id, start, end, seats }) => ({
  id,
  start: new Date(start),
  end: new Date(end),
  seats,
});

const sortExceptionsByStartTime = (a, b) => {
  return a.start.getTime() - b.start.getTime();
};

// When navigating through weekly calendar,
// we want to merge new week-related data (inProgres, error) to weeklyExceptionQueries hashmap.
const mergeToWeeklyExceptionQueries = (weeklyExceptionQueries, weekStartId, newDataProps) => {
  return weekStartId
    ? {
        weeklyExceptionQueries: {
          ...weeklyExceptionQueries,
          [weekStartId]: {
            ...weeklyExceptionQueries[weekStartId],
            ...newDataProps,
          },
        },
      }
    : {};
};
// When navigating through monthly calendar (e.g. when adding a new AvailabilityException),
// we want to merge new month-related data (inProgres, error) to monthlyExceptionQueries hashmap.
const mergeToMonthlyExceptionQueries = (monthlyExceptionQueries, monthId, newDataProps) => {
  return monthId
    ? {
        monthlyExceptionQueries: {
          ...monthlyExceptionQueries,
          [monthId]: {
            ...monthlyExceptionQueries[monthId],
            ...newDataProps,
          },
        },
      }
    : {};
};

const requestAction = (actionType) => (params) => ({ type: actionType, payload: { params } });

const successAction = (actionType) => (result) => ({ type: actionType, payload: result.data });

const errorAction = (actionType) => (payload) => ({ type: actionType, payload, error: true });

// ================ Action types ================ //

export const MARK_TAB_UPDATED = 'app/EditListingPage/MARK_TAB_UPDATED';
export const CLEAR_UPDATED_TAB = 'app/EditListingPage/CLEAR_UPDATED_TAB';
export const CLEAR_PUBLISH_ERROR = 'app/EditListingPage/CLEAR_PUBLISH_ERROR';

export const CREATE_LISTING_DRAFT_REQUEST = 'app/EditListingPage/CREATE_LISTING_DRAFT_REQUEST';
export const CREATE_LISTING_DRAFT_SUCCESS = 'app/EditListingPage/CREATE_LISTING_DRAFT_SUCCESS';
export const CREATE_LISTING_DRAFT_ERROR = 'app/EditListingPage/CREATE_LISTING_DRAFT_ERROR';

export const PUBLISH_LISTING_REQUEST = 'app/EditListingPage/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS = 'app/EditListingPage/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR = 'app/EditListingPage/PUBLISH_LISTING_ERROR';

export const UPDATE_LISTING_REQUEST = 'app/EditListingPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/EditListingPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/EditListingPage/UPDATE_LISTING_ERROR';

export const SHOW_LISTINGS_REQUEST = 'app/EditListingPage/SHOW_LISTINGS_REQUEST';
export const SHOW_LISTINGS_SUCCESS = 'app/EditListingPage/SHOW_LISTINGS_SUCCESS';
export const SHOW_LISTINGS_ERROR = 'app/EditListingPage/SHOW_LISTINGS_ERROR';

export const FETCH_EXCEPTIONS_REQUEST = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_REQUEST';
export const FETCH_EXCEPTIONS_SUCCESS = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const FETCH_EXCEPTIONS_ERROR = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_ERROR';
export const FETCH_EXTRA_EXCEPTIONS_SUCCESS =
  'app/EditListingPage/FETCH_EXTRA_AVAILABILITY_EXCEPTIONS_SUCCESS';

export const ADD_EXCEPTION_REQUEST = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_REQUEST';
export const ADD_EXCEPTION_SUCCESS = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_SUCCESS';
export const ADD_EXCEPTION_ERROR = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_ERROR';

export const DELETE_EXCEPTION_REQUEST = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_REQUEST';
export const DELETE_EXCEPTION_SUCCESS = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_SUCCESS';
export const DELETE_EXCEPTION_ERROR = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_ERROR';

export const SET_STOCK_REQUEST = 'app/EditListingPage/SET_STOCK_REQUEST';
export const SET_STOCK_SUCCESS = 'app/EditListingPage/SET_STOCK_SUCCESS';
export const SET_STOCK_ERROR = 'app/EditListingPage/SET_STOCK_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditListingPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditListingPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditListingPage/UPLOAD_IMAGE_ERROR';

export const REMOVE_LISTING_IMAGE = 'app/EditListingPage/REMOVE_LISTING_IMAGE';

export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_ERROR';

export const FETCH_MONTHLY_TIME_SLOTS_REQUEST =
  'app/EditListingPage/FETCH_MONTHLY_TIME_SLOTS_REQUEST';
export const FETCH_MONTHLY_TIME_SLOTS_SUCCESS =
  'app/EditListingPage/FETCH_MONTHLY_TIME_SLOTS_SUCCESS';
export const FETCH_MONTHLY_TIME_SLOTS_ERROR = 'app/EditListingPage/FETCH_MONTHLY_TIME_SLOTS_ERROR';

export const FETCH_LOAD_DATA_EXCEPTIONS = 'app/EditListingPage/FETCH_LOAD_DATA_EXCEPTIONS';

export const FETCH_LOAD_ALL_AVAILABILITY = 'app/EditListingPage/FETCH_LOAD_ALL_AVAILABILITY';

export const FETCH_LISTING_NUMBER_OF_TRANSACTIONS =
  'app/EditListingPage/FETCH_LISTING_NUMBER_OF_TRANSACTIONS';

const FETCH_MONTHLY_BOOKINGS_REQUEST = 'app/EditListingPage/FETCH_MONTHLY_BOOKINGS_REQUEST';
const FETCH_MONTHLY_BOOKINGS_SUCCESS = 'app/EditListingPage/FETCH_MONTHLY_BOOKINGS_SUCCESS';
const FETCH_MONTHLY_BOOKINGS_ERROR = 'app/EditListingPage/FETCH_MONTHLY_BOOKINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  listingId: null,
  publishListingError: null,
  updateListingError: null,
  showListingsError: null,
  uploadImageError: null,
  setStockError: null,
  setStockInProgress: false,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  uploadedImages: {},
  uploadedImagesOrder: [],
  removedImageIds: [],
  addExceptionError: null,
  addExceptionInProgress: false,
  weeklyExceptionQueries: {
    // '2022-12-12': { // Note: id/key is the start of the week in given time zone
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  monthlyExceptionQueries: {
    // '2022-12': {
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  allExceptions: [],
  deleteExceptionError: null,
  deleteExceptionInProgress: false,
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  listingNumberOfTransactions: 0,

  monthlyBookings: {},
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateListingError: null };
    case CLEAR_PUBLISH_ERROR:
      return { ...state, publishListingError: null };

    case CREATE_LISTING_DRAFT_REQUEST:
      return {
        ...state,
        createListingDraftInProgress: true,
        createListingDraftError: null,
        submittedListingId: null,
        listingDraft: null,
      };

    case CREATE_LISTING_DRAFT_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        createListingDraftInProgress: false,
        submittedListingId: payload.data.id,
        listingDraft: payload.data,
      };
    case CREATE_LISTING_DRAFT_ERROR:
      return {
        ...state,
        createListingDraftInProgress: false,
        createListingDraftError: payload,
      };

    case PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        listingId: payload.listingId,
        publishListingError: null,
      };
    case PUBLISH_LISTING_SUCCESS:
      return {
        ...state,
        redirectToListing: true,
        createListingDraftError: null,
        updateListingError: null,
        showListingsError: null,
        uploadImageError: null,
        createListingDraftInProgress: false,
        updateInProgress: false,
      };
    case PUBLISH_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        publishListingError: {
          listingId: state.listingId,
          error: payload,
        },
      };
    }

    case UPDATE_LISTING_REQUEST:
      return { ...state, updateInProgress: true, updateListingError: null };
    case UPDATE_LISTING_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        updateInProgress: false,
        // availabilityCalendar: { ...state.availabilityCalendar },
      };
    case UPDATE_LISTING_ERROR:
      return { ...state, updateInProgress: false, updateListingError: payload };

    case SHOW_LISTINGS_REQUEST:
      return { ...state, showListingsError: null };
    case SHOW_LISTINGS_SUCCESS: {
      const listingIdFromPayload = payload.data.id;
      const { listingId, allExceptions, weeklyExceptionQueries, monthlyExceptionQueries } = state;
      // If listing stays the same, we trust previously fetched exception data.
      return listingIdFromPayload?.uuid === state.listingId?.uuid
        ? {
            ...initialState,
            listingId,
            allExceptions,
            weeklyExceptionQueries,
            monthlyExceptionQueries,
          }
        : { ...initialState, listingId: listingIdFromPayload };
    }
    case SHOW_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, showListingsError: payload, redirectToListing: false };

    case FETCH_EXCEPTIONS_REQUEST: {
      const { monthId, weekStartId } = payload.params;
      const newData = { fetchExceptionsError: null, fetchExceptionsInProgress: true };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
          ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
          : {};
      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_SUCCESS: {
      const { exceptions, monthId, weekStartId } = payload;
      const combinedExceptions = state.allExceptions.concat(exceptions);
      const selectId = (x) => x.id;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(sortExceptionsByStartTime);
      const newData = { fetchExceptionsInProgress: false };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
          ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
          : {};
      return { ...state, allExceptions, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_ERROR: {
      const { monthId, weekStartId, error } = payload;
      const newData = { fetchExceptionsInProgress: false, fetchExceptionsError: error };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
          ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
          : {};

      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXTRA_EXCEPTIONS_SUCCESS: {
      const combinedExceptions = state.allExceptions.concat(payload.exceptions);
      const selectId = (x) => x.id;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(sortExceptionsByStartTime);
      // TODO: currently we don't handle thrown errors from these paginated calls
      return { ...state, allExceptions };
    }
    case ADD_EXCEPTION_REQUEST:
      return {
        ...state,
        addExceptionError: null,
        addExceptionInProgress: true,
      };
    case ADD_EXCEPTION_SUCCESS: {
      const exception = payload;
      const combinedExceptions = state.allExceptions.concat(exception);
      const allExceptions = combinedExceptions.sort(sortExceptionsByStartTime);
      return {
        ...state,
        allExceptions,
        addExceptionInProgress: false,
      };
    }
    case ADD_EXCEPTION_ERROR:
      return {
        ...state,
        addExceptionError: payload.error,
        addExceptionInProgress: false,
      };

    case DELETE_EXCEPTION_REQUEST:
      return {
        ...state,
        deleteExceptionError: null,
        deleteExceptionInProgress: true,
      };
    case DELETE_EXCEPTION_SUCCESS: {
      const { id } = payload;
      const allExceptions = state.allExceptions.filter((e) => e.id !== id);
      return {
        ...state,
        allExceptions,
        deleteExceptionInProgress: false,
      };
    }
    case DELETE_EXCEPTION_ERROR:
      return {
        ...state,
        deleteExceptionError: payload.error,
        deleteExceptionInProgress: false,
      };

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const uploadedImages = {
        ...state.uploadedImages,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        uploadedImages,
        uploadedImagesOrder: state.uploadedImagesOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id', attributes, type }
      const { id, ...rest } = payload;
      const uploadedImages = { ...state.uploadedImages, [id]: { id, ...rest } };
      return { ...state, uploadedImages };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const uploadedImagesOrder = state.uploadedImagesOrder.filter((i) => i !== id);
      const uploadedImages = omit(state.uploadedImages, id);
      return { ...state, uploadedImagesOrder, uploadedImages, uploadImageError: error };
    }

    case REMOVE_LISTING_IMAGE: {
      const id = payload.imageId;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.uploadedImages[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const uploadedImages = omit(state.uploadedImages, id);
      const uploadedImagesOrder = state.uploadedImagesOrder.filter((i) => i !== id);

      return { ...state, uploadedImages, uploadedImagesOrder, removedImageIds };
    }

    case SET_STOCK_REQUEST:
      return { ...state, setStockInProgress: true, setStockError: null };
    case SET_STOCK_SUCCESS:
      return { ...state, setStockInProgress: false };
    case SET_STOCK_ERROR:
      return { ...state, setStockInProgress: false, setStockError: payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case FETCH_MONTHLY_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_SUCCESS: {
      const { monthId } = payload;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_ERROR: {
      const { monthId } = payload;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }

    case FETCH_LOAD_DATA_EXCEPTIONS: {
      return {
        ...state,
        allExceptions: [],
      };
    }

    case FETCH_LOAD_ALL_AVAILABILITY: {
      return {
        ...state,
        monthlyTimeSlots: {},
      };
    }

    case FETCH_LISTING_NUMBER_OF_TRANSACTIONS: {
      return {
        ...state,
        listingNumberOfTransactions: payload,
      };
    }

    case FETCH_MONTHLY_BOOKINGS_REQUEST: {
      const monthlyBookings = {
        ...state.monthlyBookings,
        [payload]: {
          fetchMonthlyBookingsError: null,
          fetchMonthlyBookingsInProgress: true,
        },
      };
      return { ...state, monthlyBookings };
    }

    case FETCH_MONTHLY_BOOKINGS_SUCCESS: {
      const { monthId, bookings } = payload;
      const monthlyBookings = {
        ...state.monthlyBookings,
        [monthId]: {
          fetchMonthlyBookingsInProgress: false,
          bookings,
        },
      };
      return { ...state, monthlyBookings };
    }

    case FETCH_MONTHLY_BOOKINGS_ERROR: {
      const { monthId, error } = payload;
      const monthlyBookings = {
        ...state.monthlyBookings,
        [monthId]: {
          fetchMonthlyBookingsInProgress: false,
          fetchMonthlyBookingsError: error,
        },
      };
      return { ...state, monthlyBookings };
    }

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const markTabUpdated = (tab) => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

export const removeListingImage = (imageId) => ({
  type: REMOVE_LISTING_IMAGE,
  payload: { imageId },
});

export const clearPublishError = () => ({
  type: CLEAR_PUBLISH_ERROR,
});

// All the action creators that don't have the {Success, Error} suffix
// take the params object that the corresponding SDK endpoint method
// expects.

// SDK method: ownListings.create
export const createListingDraftRequest = requestAction(CREATE_LISTING_DRAFT_REQUEST);
export const createListingDraftSuccess = successAction(CREATE_LISTING_DRAFT_SUCCESS);
export const createListingDraftError = errorAction(CREATE_LISTING_DRAFT_ERROR);

// SDK method: ownListings.publish
export const publishListingRequest = requestAction(PUBLISH_LISTING_REQUEST);
export const publishListingSuccess = successAction(PUBLISH_LISTING_SUCCESS);
export const publishListingError = errorAction(PUBLISH_LISTING_ERROR);

// SDK method: ownListings.update
export const updateListingRequest = requestAction(UPDATE_LISTING_REQUEST);
export const updateListingSuccess = successAction(UPDATE_LISTING_SUCCESS);
export const updateListingError = errorAction(UPDATE_LISTING_ERROR);

// SDK method: ownListings.show
export const showListingsRequest = requestAction(SHOW_LISTINGS_REQUEST);
export const showListingsSuccess = successAction(SHOW_LISTINGS_SUCCESS);
export const showListingsError = errorAction(SHOW_LISTINGS_ERROR);

// SDK method: images.upload
export const uploadImageRequest = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);

// SDK method: stock.compareAndSet
export const setStockRequest = requestAction(SET_STOCK_REQUEST);
export const setStockSuccess = successAction(SET_STOCK_SUCCESS);
export const setStockError = errorAction(SET_STOCK_ERROR);

// listingsApi.exceptions.list
export const fetchAvailabilityExceptionsRequest = requestAction(FETCH_EXCEPTIONS_REQUEST);
export const fetchAvailabilityExceptionsSuccess = successAction(FETCH_EXCEPTIONS_SUCCESS);
export const fetchAvailabilityExceptionsError = errorAction(FETCH_EXCEPTIONS_ERROR);
// Add extra data from additional pages
export const fetchExtraAvailabilityExceptionsSuccess = successAction(
  FETCH_EXTRA_EXCEPTIONS_SUCCESS
);

// listingsApi.exceptions.create
export const addAvailabilityExceptionRequest = requestAction(ADD_EXCEPTION_REQUEST);
export const addAvailabilityExceptionSuccess = successAction(ADD_EXCEPTION_SUCCESS);
export const addAvailabilityExceptionError = errorAction(ADD_EXCEPTION_ERROR);

// listingsApi.exceptions.delete
export const deleteAvailabilityExceptionRequest = requestAction(DELETE_EXCEPTION_REQUEST);
export const deleteAvailabilityExceptionSuccess = successAction(DELETE_EXCEPTION_SUCCESS);
export const deleteAvailabilityExceptionError = errorAction(DELETE_EXCEPTION_ERROR);

export const savePayoutDetailsRequest = requestAction(SAVE_PAYOUT_DETAILS_REQUEST);
export const savePayoutDetailsSuccess = successAction(SAVE_PAYOUT_DETAILS_SUCCESS);
export const savePayoutDetailsError = errorAction(SAVE_PAYOUT_DETAILS_ERROR);

export const fetchMonthlyTimeSlotsRequest = (monthId) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchMonthlyTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchMonthlyTimeSlotsError = (monthId, error) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchLoadDataExceptionsRequest = () => ({
  type: FETCH_LOAD_DATA_EXCEPTIONS,
});

export const fetchLoadAllAvailability = () => ({
  type: FETCH_LOAD_ALL_AVAILABILITY,
});

export const fetchListingNumberOfTransactions = (numberOfTransactions) => ({
  type: FETCH_LISTING_NUMBER_OF_TRANSACTIONS,
  payload: numberOfTransactions,
});

const fetchMonthlyBookingsRequest = (monthId) => ({
  type: FETCH_MONTHLY_BOOKINGS_REQUEST,
  payload: monthId,
});

const fetchMonthlyBookingsSuccess = (monthId, bookings) => ({
  type: FETCH_MONTHLY_BOOKINGS_SUCCESS,
  payload: { monthId, bookings },
});

const fetchMonthlyBookingsError = (monthId, error) => ({
  type: FETCH_MONTHLY_BOOKINGS_ERROR,
  error: true,
  payload: { monthId, error },
});

// ================ Thunk ================ //

// Includes and image variants are picked by the server.
export function requestShowListing(actionPayload) {
  return (dispatch) => {
    dispatch(showListingsRequest(actionPayload));
    return listingsApi.ownListings
      .show(actionPayload)
      .then((response) => {
        // EditListingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.EditListingPage (user will be redirected away)
        dispatch(showListingsSuccess(response));
        return response;
      })
      .catch((e) => dispatch(showListingsError(storableError(e))));
  };
}

// Set stock if requested among listing update info
export function compareAndSetStock(listingId, oldTotal, newTotal) {
  return (dispatch, getState, sdk) => {
    dispatch(setStockRequest());

    return sdk.stock
      .compareAndSet({ listingId, oldTotal, newTotal }, { expand: true })
      .then((response) => {
        // NOTE: compareAndSet returns the stock resource of the listing.
        // We update client app's internal state with these updated API entities.
        dispatch(addMarketplaceEntities(response));
        dispatch(setStockSuccess(response));
      })
      .catch((e) => {
        log.error(e, 'update-stock-failed', { listingId, oldTotal, newTotal });
        return dispatch(setStockError(storableError(e)));
      });
  };
}

// Helper function to make compareAndSetStock call if stock update is needed.
const updateStockOfListingMaybe = (listingId, stockTotals, dispatch) => {
  const { oldTotal, newTotal } = stockTotals || {};
  // Note: newTotal and oldTotal must be given, but oldTotal can be null
  const hasStockTotals = newTotal >= 0 && typeof oldTotal !== 'undefined';

  if (listingId && hasStockTotals) {
    return dispatch(compareAndSetStock(listingId, oldTotal, newTotal));
  }
  return Promise.resolve();
};

// Create listing in draft state: `pendingApproval` with the server's draft marker (CH-574).
// Includes and image variants are picked by the server.
// NOTE: we want to keep it possible to include stock management field to the first wizard form.
// this means that there needs to be a sequence of calls: create, then set stock.
export function requestCreateListingDraft(data) {
  return (dispatch) => {
    dispatch(createListingDraftRequest(data));
    const { stockUpdate, images, ...rest } = data;

    // If images should be saved, create array out of the image UUIDs for the API call
    // Note: in this template, image upload is not happening at the same time as listing creation.
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingValues = { ...imageProperty, ...rest };

    let createDraftResponse = null;
    return listingsApi.ownListings
      .create(ownListingValues)
      .then((response) => {
        createDraftResponse = response;
        const listingId = response.data.data.id;
        // If stockUpdate info is passed through, update stock
        return updateStockOfListingMaybe(listingId, stockUpdate, dispatch);
      })
      .then(() => {
        // Modify store to understand that we have created listing and can redirect away
        dispatch(createListingDraftSuccess(createDraftResponse));
        return createDraftResponse;
      })
      .catch((e) => {
        log.error(e, 'create-listing-draft-failed', { listingData: data });
        return dispatch(createListingDraftError(storableError(e)));
      });
  };
}

const validException = (exception) => {
  const { start, end } = exception;
  return moment(end).isSameOrAfter(start) && moment(start).isSameOrAfter(moment());
};

const captureAwaitingStripe = async (dispatch, listingId) => {
  window.posthog?.capture?.('onboarding transition connect-stripe');

  try {
    await dispatch(fetchCurrentUser({ enforce: true }));
  } catch (e) {
    log.error(e, 'publish-listing-fetch-current-user-failed', { listingId });
  }

  window.posthog?.capture?.('onboarding listing published awaiting stripe', {
    listing_id: listingId.uuid,
  });
};

// A listing in Sharetribe's real `draft` state predates CH-574's v2 drafts. Only its
// provider made it, and the Integration API cannot publish a `draft`, so it stays on the
// client SDK. The v2 update after it regenerates the slug and, without Stripe, sets the
// private flags in the same write.
const publishLegacyDraft = async (listingId, withoutStripe, sdk) => {
  await sdk.ownListings.publishDraft({ id: listingId }, { expand: true });

  // Listing is now published on Sharetribe. Errors from here are logged but the publish
  // still succeeds, so the wizard doesn't re-offer the publish button.
  let response = null;
  try {
    const publicData = withoutStripe ? { isPrivate: true, awaitingStripeConnect: true } : {};
    response = await listingsApi.ownListings.update({ id: listingId, publicData });
  } catch (e) {
    log.error(e, 'publish-listing-post-publish-failed', { listingId });
    response = await listingsApi.ownListings.show({ id: listingId });
  }

  if (withoutStripe) {
    try {
      await updateUserProfile({ publicData: { onboardingPosition: 'connect-stripe' } });
    } catch (e) {
      log.error(e, 'publish-listing-without-stripe-post-publish-failed', { listingId });
    }
  }

  return response;
};

// `withoutStripe` is the wizard's Stripe decision and applies to a legacy `draft` only.
// A v2 draft's Stripe branch is decided on the server, which moves `onboardingPosition`
// itself and answers with `awaitingStripeConnect` set when it published privately.
export const requestPublishListing =
  (listingId, { withoutStripe = false } = {}) =>
  async (dispatch, getState, sdk) => {
    dispatch(publishListingRequest(listingId));

    const [listing] = getMarketplaceEntities(getState(), [{ id: listingId, type: 'ownListing' }]);
    const isLegacyDraft = listing?.attributes?.state === LISTING_STATE_DRAFT;

    let response = null;
    try {
      response = isLegacyDraft
        ? await publishLegacyDraft(listingId, withoutStripe, sdk)
        : await listingsApi.ownListings.publish({ id: listingId });
    } catch (e) {
      log.error(e, 'publish-listing-failed', { listingId });
      return dispatch(publishListingError(storableError(e)));
    }

    dispatch(addMarketplaceEntities(response));

    const { awaitingStripeConnect } = response.data.data.attributes.publicData || {};
    if (awaitingStripeConnect) {
      await captureAwaitingStripe(dispatch, listingId);
    }

    return dispatch(publishListingSuccess(response));
  };

// Images return imageId which we need to map with previously generated temporary id
// The route is per listing; the photos form doesn't pass one, so it comes from the loaded listing.
export function requestImageUpload(actionPayload) {
  return (dispatch, getState) => {
    const { id, file } = actionPayload;
    const listingId = actionPayload.listingId ?? getState().EditListingPage.listingId;

    dispatch(uploadImageRequest(actionPayload));
    return listingsApi.images
      .upload({ listingId, file })
      .then((resp) => {
        const img = resp.data.data;
        // Uploaded image has an existing id that refers to file
        // The UUID was created as a consequence of this upload call - it's saved to imageId property
        return dispatch(
          uploadImageSuccess({ data: { ...img, id, imageId: img.id, file: actionPayload.file } })
        );
      })
      .catch((e) => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

// The server keeps the `publicData.exceptions` mirror in step on create and delete.
export const requestAddAvailabilityException = (params) => (dispatch) => {
  dispatch(addAvailabilityExceptionRequest(params));

  return listingsApi.exceptions
    .create(params)
    .then(({ exception }) => {
      return dispatch(addAvailabilityExceptionSuccess({ data: toListingException(exception) }));
    })
    .catch((e) => {
      dispatch(addAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

export const requestDeleteAvailabilityException = (params) => (dispatch, getState) => {
  dispatch(deleteAvailabilityExceptionRequest(params));

  const listingId = params.listingId ?? getState().EditListingPage.listingId;

  return listingsApi.exceptions
    .delete({ listingId, id: params.id })
    .then(({ exception }) => {
      return dispatch(deleteAvailabilityExceptionSuccess({ data: exception }));
    })
    .catch((e) => {
      dispatch(deleteAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

// One request for the lot: deleted one by one, each cost about six Sharetribe calls.
export const requestDeleteAvailabilityExceptions =
  ({ listingId, ids }) =>
  (dispatch) => {
    dispatch(deleteAvailabilityExceptionRequest({ listingId, ids }));

    return listingsApi.exceptions
      .deleteMany({ listingId, ids })
      .then(({ exceptions }) => {
        exceptions.forEach((exception) =>
          dispatch(deleteAvailabilityExceptionSuccess({ data: exception }))
        );
      })
      .catch((e) => {
        dispatch(deleteAvailabilityExceptionError({ error: storableError(e) }));
        throw e;
      });
  };

// The server merges every page, so one response holds the whole range.
export const requestFetchAvailabilityExceptions = (params) => (dispatch) => {
  const { listingId, start, end, timeZone, isWeekly } = params;
  const timeUnitIdProp = isWeekly
    ? { weekStartId: stringifyDateToISO8601(start) }
    : { monthId: monthIdString(start, timeZone) };
  dispatch(fetchAvailabilityExceptionsRequest(timeUnitIdProp));

  return listingsApi.exceptions
    .list({ listingId, start, end })
    .then((response) => {
      const availabilityExceptions = response.exceptions.map(toListingException);

      return dispatch(
        fetchAvailabilityExceptionsSuccess({
          data: { ...timeUnitIdProp, exceptions: availabilityExceptions },
        })
      );
    })
    .catch((e) => {
      return dispatch(
        fetchAvailabilityExceptionsError({ ...timeUnitIdProp, error: storableError(e) })
      );
    });
};

const timeSlotsRequest = (params) => (dispatch, getState, sdk) => {
  const currentOwnListings = getMarketplaceEntities(getState(), [
    {
      id: params.listingId,
      type: 'ownListing',
    },
  ]);
  const currentListing = currentOwnListings[0];
  return sdk.timeslots.query(params).then((response) => {
    const denormalisedResponses = denormalisedResponseEntities(response);
    return splitMergedTimeSlot({
      availabilityPlan: currentListing?.attributes?.publicData?.availabilityPlan,
      timeSlots: denormalisedResponses,
    });
  });
};

export const fetchTimeSlots =
  (listingId, start, end, timeZone, options) => async (dispatch, getState) => {
    const { extraQueryParams = null } = options || {};

    // The maximum pagination page size for timeSlots is 500
    const extraParams = extraQueryParams || {
      perPage: 500,
      page: 1,
    };

    const monthId = monthIdString(start, timeZone);
    dispatch(fetchMonthlyTimeSlotsRequest(monthId));

    try {
      const timeSlots = await dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }));
      dispatch(fetchMonthlyTimeSlotsSuccess(monthId, timeSlots));
      return timeSlots;
    } catch (e) {
      dispatch(fetchMonthlyTimeSlotsError(monthId, storableError(e)));
      return [];
    }
  };

export const fetchBookings =
  (listingId, start, end, timeZone) => async (dispatch, getState, sdk) => {
    const monthId = monthIdString(start, timeZone);
    dispatch(fetchMonthlyBookingsRequest(monthId));

    try {
      const bookings = await queryAllPages({
        sdkModel: sdk.bookings,
        params: {
          listingId,
          start,
          end,
          state: 'accepted, pending',
        },
      });
      dispatch(fetchMonthlyBookingsSuccess(monthId, bookings));
      return bookings;
    } catch (e) {
      dispatch(fetchMonthlyBookingsError(monthId, storableError(e)));
      return [];
    }
  };

export const fetchLoadAvailability = (listing, firstDayOfWeek) => (dispatch) => {
  const hasWindow = typeof window !== 'undefined';
  const hasTimeZone = listing?.attributes?.availabilityPlan?.timezone;

  if (!hasWindow || !listing?.id || !hasTimeZone) {
    return Promise.all([]);
  }

  dispatch(fetchLoadAllAvailability());

  const {
    isRecurring,
    firstSession,
    recurringOption,
    numberOfSessions,
    endDate,
    endOption = 'after-sessions',
    availabilityPlan,
    startTime,
    endTime,
    exceptions,
  } = listing?.attributes?.publicData || {};

  if (isRecurring !== 'yes' || !recurringOption === 'custom-schedule') {
    return Promise.all([]);
  }

  const sessions =
    getSession({
      isRecurring,
      firstSession,
      recurringOption,
      numberOfSessions,
      endDate,
      endOption,
      availabilityPlan,
      firstDayOfWeek,
      startTime,
      endTime,
      exceptions,
    }) ?? [];

  const lastSession = sessions[sessions.length - 1];
  const { rawDate: lastSessionRawDate } = lastSession;
  const now = moment();
  const lastSessionMoment = moment(lastSessionRawDate);
  const timeZone = listing?.attributes?.availabilityPlan?.timezone || getDefaultTimeZoneOnBrowser();
  const isPublished = listing?.attributes?.state === LISTING_STATE_PUBLISHED;

  if (lastSessionMoment.isAfter(now)) {
    const monthsList = getListOfMonthsUntilLastSession(now, lastSessionMoment);
    monthsList.forEach((month) => {
      const currentMonth = moment.utc(month, 'YYYY-MM');
      const start = now.isAfter(moment.tz(currentMonth.utc(), timeZone))
        ? now.toDate()
        : moment
            .tz(currentMonth.utc().startOf('month'), timeZone)
            .set({
              hour: 0,
              minute: 0,
              second: 0,
              millisecond: 0,
            })
            .toDate();
      const end = currentMonth.clone().utc().endOf('month').toDate();
      if (isPublished) {
        dispatch(
          fetchTimeSlots(listing.id, start, end, timeZone, {
            intervalDuration: 'P1D',
            maxPerInterval: 100,
            perPage: 31,
            page: 1,
          })
        );
        dispatch(fetchBookings(listing.id, start, end, timeZone));
      } else {
        const [startHour, startMinute] = startTime.split(':');
        const [endHour, endMinute] = endTime.split(':');

        const generatedTimeSlotsInMonth = sessions
          .filter((session) => {
            return moment(session.rawDate).isSame(currentMonth, 'month');
          })
          .map((session) => {
            return {
              id: new UUID(nanoid()),
              type: 'timeSlot',
              attributes: {
                type: 'time-slot/time',
                start: moment(session.rawDate)
                  .tz(timeZone)
                  .set({
                    hour: startHour,
                    minute: startMinute,
                    second: 0,
                    millisecond: 0,
                  })
                  .toDate(),
                end: moment(session.rawDate)
                  .tz(timeZone)
                  .set({
                    hour: endHour,
                    minute: endMinute,
                    second: 0,
                    millisecond: 0,
                  })
                  .toDate(),
                seats: 1,
              },
            };
          });
        const monthId = monthIdString(
          moment
            .tz(currentMonth.utc().startOf('month'), timeZone)
            .set({
              hour: 0,
              minute: 0,
              second: 0,
              millisecond: 0,
            })
            .toDate(),
          timeZone
        );
        dispatch(fetchMonthlyTimeSlotsRequest(monthId));
        dispatch(fetchMonthlyTimeSlotsSuccess(monthId, generatedTimeSlotsInMonth));
      }
    });
  }

  return Promise.all([]);
};

// Helper function for loadData call.
const fetchLoadDataExceptions = (listing, search, firstDayOfWeek) => (dispatch) => {
  const hasWindow = typeof window !== 'undefined';
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone = listing?.attributes?.availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  // Note: listing needs to have time zone set!
  if (hasWindow && listing.id && hasTimeZone) {
    dispatch(fetchLoadDataExceptionsRequest());
    const listingId = listing.id;
    // If the listing doesn't have availabilityPlan yet
    // use the defaul timezone
    const timezone = listing.attributes.availabilityPlan?.timezone || getDefaultTimeZoneOnBrowser();
    const todayInListingsTZ = getStartOf(new Date(), 'day', timezone);

    const oneYearFromToDay = moment(todayInListingsTZ).add('365', 'days').startOf('day').toDate();

    return Promise.all([
      dispatch(
        requestFetchAvailabilityExceptions({
          listingId,
          start: todayInListingsTZ,
          end: oneYearFromToDay,
        })
      ),
    ]).then(() => {
      dispatch(fetchLoadAvailability(listing, firstDayOfWeek));
    });
  }

  // By default return an empty array
  return Promise.all([]);
};

export const fetchListingTransactions = (listingId) => async (dispatch) => {
  try {
    const { count } = await listingsApi.transactions.count({ listingId });
    const numberOfTransactions = count ?? 0;

    dispatch(fetchListingNumberOfTransactions(numberOfTransactions));
    return numberOfTransactions;
  } catch (e) {
    log.error(e, 'fetch-listing-number-of-transactions-failed', { listingId });
    dispatch(fetchListingNumberOfTransactions(0));
    return 0;
  }
};

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
// NOTE: what comes to stock management, this follows the same pattern used in create listing call
export function requestUpdateListing(tab, data, config) {
  return async (dispatch, getState) => {
    dispatch(updateListingRequest(data));
    const { id, stockUpdate, images, firstException, secondException, ...rest } = data;

    // If images should be saved, create array out of the image UUIDs for the API call
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    // The server stamps `authorName` and regenerates a published listing's slug.
    const ownListingUpdateValues = { id, ...imageProperty, ...rest };

    const state = getState();
    const existingTimeZone =
      state.marketplaceData.entities.ownListing[id.uuid]?.attributes?.availabilityPlan?.timezone;
    const includedTimeZone = rest?.availabilityPlan?.timezone;

    // Note: if update values include stockUpdate, we'll do that first
    // That way we get updated currentStock info among ownListings.update
    return updateStockOfListingMaybe(id, stockUpdate, dispatch)
      .then(() => listingsApi.ownListings.update(ownListingUpdateValues))
      .then(async (response) => {
        dispatch(updateListingSuccess(response));
        dispatch(addMarketplaceEntities(response));
        dispatch(markTabUpdated(tab));

        // If time zone has changed, we need to fetch exceptions again
        // since week and month boundaries might have changed. Skipped when exceptions
        // are being rewritten below, which refetches once they're done: fetching here
        // too doubled the calls and raced the deletes.
        const isRewritingExceptions = !!(firstException || secondException);
        if (!!includedTimeZone && includedTimeZone !== existingTimeZone && !isRewritingExceptions) {
          const searchString = '';
          const { firstDayOfWeek } = config.localization;
          const listing = response.data.data;
          dispatch(fetchLoadDataExceptions(listing, searchString, firstDayOfWeek));
        }

        if (firstException || secondException) {
          const listing = response.data.data;
          const timezone =
            listing.attributes.availabilityPlan?.timezone || getDefaultTimeZoneOnBrowser();
          const { exceptions = [] } = await listingsApi.exceptions.list({
            listingId: id,
            start: moment().startOf('day').toDate(),
            end: moment().add(364, 'days').endOf('day').toDate(),
          });
          // firstException's own id is usually in the list already; the server dedupes.
          const ids = [...exceptions.map((exception) => exception.id), firstException?.id].filter(
            (exceptionId) => exceptionId && exceptionId !== DUPLICATED_EXCEPTION_ID
          );
          if (ids.length) {
            await dispatch(requestDeleteAvailabilityExceptions({ listingId: id, ids }));
          }

          if (firstException && validException(firstException)) {
            await dispatch(
              requestAddAvailabilityException({
                listingId: id.uuid,
                start: firstException.start,
                end: firstException.end,
                seats: firstException.seats,
              })
            );
          }

          if (secondException && validException(secondException)) {
            await dispatch(
              requestAddAvailabilityException({
                listingId: id.uuid,
                start: secondException.start,
                end: secondException.end,
                seats: secondException.seats,
              })
            );
          }
          dispatch(fetchLoadDataExceptions(listing, '', config.localization.firstDayOfWeek));
        }

        return response;
      })
      .catch((e) => {
        log.error(e, 'update-listing-failed', { listingData: data });
        return dispatch(updateListingError(storableError(e)));
      });
  };
}

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then((response) => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

export const updatePriceAfterAvailabilityOrExceptionsChanged =
  (listingId, config) => async (dispatch) => {
    try {
      const { currency } = config;
      const listingResponse = await listingsApi.ownListings.show({ id: listingId });
      const [listing] = denormalisedResponseEntities(listingResponse);
      const {
        payAsYouGo,
        blockBooking,
        singleEvent,
        pricingOptions,
        listingType,
        exceptions = [],
        endOption,
        endDate,
        firstSession,
        recurringOption,
        numberOfSessions,
      } = listing.attributes.publicData;
      const numberOfExceptions = Math.max(0, exceptions.length - 2);
      const curNumberOfSessions =
        (endOption === END_OPTION_SPECIFIC_DATE
          ? generateNumberOfSessionFromEndDate(endDate, firstSession, recurringOption)
          : numberOfSessions) - numberOfExceptions;
      const minPrice = getCheapestPrice({
        childrenPayAsYouGo: payAsYouGo?.childrenPrice,
        childrenBlockBooking: blockBooking?.childrenPrice,
        childrenSingleEvent: singleEvent?.childrenPrice,
        adultPayAsYouGo: payAsYouGo?.adultPrice,
        adultBlockBooking: blockBooking?.adultPrice,
        adultSingleEvent: singleEvent?.adultPrice,
        numberOfSessions: curNumberOfSessions,
        pricingOptions: pricingOptions || [],
        listingType,
        payAsYouGoCustomTicket: payAsYouGo?.customTicket,
        blockBookingCustomTicket: blockBooking?.customTicket,
        singleEventCustomTicket: singleEvent?.customTicket,
        marketplaceCurrency: currency,
      });
      const ticketTypePrices = getTicketTypesPrices({
        ...listing.attributes.publicData,
        numberOfSessions: curNumberOfSessions,
      });
      const response = await listingsApi.ownListings.update({
        id: listingId,
        price: minPrice,
        publicData: {
          ...ticketTypePrices,
        },
      });
      dispatch(addMarketplaceEntities(response));
      return response;
    } catch (e) {
      console.error(e);
    }
  };

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  dispatch(clearUpdatedTab());
  dispatch(clearPublishError());
  const { id, type } = params;
  const fetchCurrentUserOptions = {
    updateNotifications: false,
  };

  if (type === 'new') {
    // No need to listing data when creating a new listing
    return Promise.all([dispatch(fetchCurrentUser(fetchCurrentUserOptions))])
      .then((response) => {
        const { currentUser } = getState().user;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }
        return response;
      })
      .catch((e) => {
        throw e;
      });
  }

  const payload = { id: new UUID(id) };
  return Promise.all([
    dispatch(requestShowListing(payload)),
    dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
  ])
    .then((response) => {
      const { currentUser } = getState().user;

      // Do not fetch extra information if user is in pending-approval state.
      if (isUserAuthorized(currentUser)) {
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }

        // Because of two dispatch functions, response is an array.
        // We are only interested in the response from requestShowListing here,
        // so we need to pick the first one
        const listing = response[0]?.data?.data;
        const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias;
        if (listing && isBookingProcessAlias(transactionProcessAlias)) {
          dispatch(fetchLoadDataExceptions(listing, search, config.localization.firstDayOfWeek));
        }
      }

      return response;
    })
    .catch((e) => {
      throw e;
    });
};

export const selectEditListingMonthlyTimeSlots = (state) => {
  return state.EditListingPage.monthlyTimeSlots;
};

export const selectDeleteExceptionInProgress = (state) => {
  return state.EditListingPage.deleteExceptionInProgress;
};

export const selectDeleteExceptionError = (state) => {
  return state.EditListingPage.deleteExceptionError;
};

export const selectCreateExceptionInProgress = (state) => {
  return state.EditListingPage.createExceptionInProgress;
};

export const selectCreateExceptionError = (state) => {
  return state.EditListingPage.createExceptionError;
};

export const selectAllExceptions = (state) => {
  return state.EditListingPage.allExceptions;
};

export const selectListingNumberOfTransactions = (state) => {
  return state.EditListingPage.listingNumberOfTransactions;
};

export const selectEditListingMonthlyBookings = (state) => {
  return state.EditListingPage.monthlyBookings;
};
